// Match States — D3 SVG radial "clock" chart (Phase 3).
//
// Six 15-min time bands as sectors; each sector is N concentric rings (one per
// match in the window) coloured by the team's W/D/L state during that band.
// React owns the container + <svg>; D3 draws inside a useEffect — a full
// clear+redraw, matching the legacy (no animation, it rebuilds each render).
// ResizeObserver cleaned up in the effect return. Emits the same class names +
// DOM as the legacy renderer (verified by the A/B). Fixture + timeline + team +
// ha from the context.

import { useEffect, useRef } from "react";
import { select } from "d3-selection";
import { arc } from "d3-shape";
import { shapeMatchStates, type MatchStatesShape } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

const TAU = 2 * Math.PI;
const RED = "#e74c3c";
const GREY = "#888888";
const GREEN = "#2ecc71";

// Each Match States instance needs its own mask id (several can be on the canvas).
let maskSeq = 0;

function draw(svgEl: SVGSVGElement, wrap: HTMLElement, shape: MatchStatesShape, maskId: string): void {
  const cW = wrap.clientWidth;
  const cH = wrap.clientHeight;
  if (cW < 20 || cH < 20) return; // not laid out yet — the ResizeObserver retries

  const cx = cW / 2;
  const cy = cH / 2;
  const LABEL_PAD = 24;
  const R = Math.min(cW * 0.375, cH * 0.5) - LABEL_PAD - 4;
  const CENTER_R = Math.max(5, R * 0.07);
  const TOTAL_W = R - CENTER_R;
  const sAngle = TAU / 6;
  const N = shape.n;
  const ringW = TOTAL_W / N;

  const svg = select(svgEl);
  svg.attr("width", cW).attr("height", cH).attr("viewBox", `0 0 ${cW} ${cH}`);
  svg.selectAll("*").remove();

  const g = svg
    .append("g")
    .attr("transform", `translate(${cx},${cy})`);

  // The ring boundaries, sector spokes, half-time line and centre are CUT OUT
  // of the rings with a mask (black = cut), not painted in a "background"
  // colour — so the gaps always show the real tile behind them (dark gradient,
  // light tile or a colour wash) and can't go stale when the theme changes.
  const PAD = R + 10;
  const mask = svg
    .append("defs")
    .append("mask")
    .attr("id", maskId)
    .attr("maskUnits", "userSpaceOnUse")
    .attr("x", -PAD)
    .attr("y", -PAD)
    .attr("width", PAD * 2)
    .attr("height", PAD * 2);
  mask.append("rect")
    .attr("x", -PAD).attr("y", -PAD).attr("width", PAD * 2).attr("height", PAD * 2)
    .attr("fill", "white");
  const rings = g.append("g").attr("mask", `url(#${maskId})`);
  const arcGen = arc();

  // Each sector: N rings, one per match, coloured by W/D/L state. Rings fill
  // centre→edge in the order loss (red) → draw (grey) → win (green).
  shape.sectors.forEach((sector, si) => {
    const sa = si * sAngle;
    const ea = (si + 1) * sAngle;
    const { lCount, dCount } = sector;
    for (let j = 0; j < N; j++) {
      const rIn = CENTER_R + j * ringW;
      const rOut = CENTER_R + (j + 1) * ringW;
      const fill = j < lCount ? RED : j < lCount + dCount ? GREY : GREEN;
      rings.append("path")
        .attr("class", "bento-fill")
        .attr(
          "d",
          arcGen({ innerRadius: rIn, outerRadius: rOut, startAngle: sa, endAngle: ea })
        )
        .attr("fill", fill);
    }
  });

  // Concentric ring boundaries — one per match (cut out).
  for (let j = 1; j < N; j++) {
    mask.append("circle")
      .attr("r", CENTER_R + j * ringW)
      .attr("fill", "none")
      .attr("stroke", "black")
      .attr("stroke-width", Math.max(0.4, ringW * 0.18));
  }

  // Sector dividers — straight radial lines, consistent width at all radii.
  for (let i = 0; i < 6; i++) {
    const a = i * sAngle;
    const x1 = Math.sin(a) * CENTER_R;
    const y1 = -Math.cos(a) * CENTER_R;
    const x2 = Math.sin(a) * R;
    const y2 = -Math.cos(a) * R;
    mask.append("line")
      .attr("x1", x1)
      .attr("y1", y1)
      .attr("x2", x2)
      .attr("y2", y2)
      .attr("stroke", "black")
      .attr("stroke-width", 3);
  }

  // First/second half divider — spans the circle diameter (cut out).
  mask.append("line")
    .attr("x1", 0).attr("y1", -R).attr("x2", 0).attr("y2", R)
    .attr("stroke", "black")
    .attr("stroke-width", 3);

  // Center cap (cut out).
  mask.append("circle").attr("r", CENTER_R).attr("fill", "black");

  // Time band labels.
  const fontSize = Math.max(8, Math.min(11, cW * 0.038));
  const R_LABEL = R + LABEL_PAD * 0.68;
  shape.sectors.forEach((sector, si) => {
    const mid = (si + 0.5) * sAngle;
    const lx = Math.sin(mid) * R_LABEL;
    const ly = -Math.cos(mid) * R_LABEL;
    const anchor = lx > 3 ? "start" : lx < -3 ? "end" : "middle";
    g.append("text")
      .attr("x", lx)
      .attr("y", ly)
      .attr("text-anchor", anchor)
      .attr("dominant-baseline", "middle")
      .attr("fill", "#aaa")
      .attr("font-size", fontSize)
      .attr("font-weight", 600)
      .text(sector.band);
  });
}

export interface MatchStatesProps {
  fixture: string;
  timeline?: string | null;
  team?: string | null;
  ha?: string | null;
}

export default function MatchStates({
  fixture,
  timeline,
  team,
  ha,
}: MatchStatesProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const maskId = useRef(`ms-gaps-${++maskSeq}`).current;

  const { data, isPending, isError, refetch } = useWidgetData(
    "match-states",
    useFixtureKey(fixture)
  );

  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const homeTeam = (parts[0] ?? "").trim();
  const chosen = team || homeTeam;
  const shape = shapeMatchStates(data ?? {}, chosen, ha, timeline);
  const sig = JSON.stringify(shape);

  // Hooks stay above the early returns; `sig` folds in the fetched data, so the
  // effect redraws once it resolves (empty → populated). While pending the svg
  // ref isn't mounted, so `draw` bails.
  useEffect(() => {
    const wrap = wrapRef.current;
    const svgEl = svgRef.current;
    if (!wrap || !svgEl || !shape) return;
    const render = () => draw(svgEl, wrap, shape, maskId);
    render();

    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(render);
    ro.observe(wrap);
    return () => ro.disconnect();
    // `sig` captures the shaped sectors + ring count (the complete draw input).
  }, [sig]);

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  if (!shape) {
    return (
      <div className="data-wrapper ms-wrapper">
        <div className="module-empty">No data</div>
      </div>
    );
  }

  return (
    <div className="data-wrapper ms-wrapper" ref={wrapRef}>
      <svg className="ms-svg" ref={svgRef} />
    </div>
  );
}
